import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import useAxios from 'axios-hooks'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/ja'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

dayjs.locale('ja')
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')

import './styles/styles.css'

interface EventType {
  start: string
  end: string
  status?: 'close' | 'open'

}

interface EventDataType {
  date: string
  events?: EventType[]
}

interface ScheduleType {
  data: EventDataType[]
}

const apiUrl = 'https://us-central1-truestate-app.cloudfunctions.net/api'
const maxDate = 7
const dateFormat = 'YYYY-MM-DD'
const initialStartDate = dayjs().format(dateFormat)
const endDate = dayjs(initialStartDate).add(20, 'day').format(dateFormat)
const startTime = dayjs(`${initialStartDate} 09:00`)

const App = () => {

  const [startDate, setStartDate] = useState<string>(initialStartDate)
  const [dateRange, setDateRange] = useState<string[]>([])
  const [timeRange, setTimeRange] = useState<string[]>([])
  const [events, setEvents] = useState<ScheduleType>()
  const [enableModal, setEnableModal] = useState(false)
  const [selectDatetime, setSelectDatetime] = useState<string | undefined>()
  const [user, setUser] = useState<{ name?: string, email?: string }>({ name: undefined, email: undefined })
  const [enableSubmit, setEnableSubmit] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [reserveSuccess, setReserveSuccess] = useState<boolean>(false)

  const [{ data: schedule, loading }, refetch] = useAxios(
    `${apiUrl}/events`
  )


  useEffect(() => {
    if (loading) return
    const data: ScheduleType = {
      data: schedule.data
    }
    setEvents(data)
  }, [schedule, loading])

  useEffect(() => {
    setDateRange(
      Array(maxDate).fill(undefined).map((_, index) => dayjs(startDate).add(index, 'day').format(dateFormat))
    )
  }, [startDate])

  useEffect(() => {
    setTimeRange(Array(23).fill(undefined).map((_, index) =>
      startTime.add(30 * index, 'minute').format('HH:mm')
    ))
  }, [])

  useEffect(() => {
    if (user.name && user.email) {
      setEnableSubmit(true)
    } else {
      setEnableSubmit(false)
    }
  }, [user])

  const handleClickPrevWeek = useCallback(() => {
    if (dayjs(dateRange[0]).diff(initialStartDate, 'day') <= 0) return
    setStartDate(dayjs(startDate).add(-1, 'week').format(dateFormat))
  }, [startDate, dateRange])

  const handleClickNextWeek = useCallback(() => {
    if (dayjs(endDate).diff(dateRange[dateRange.length - 1], 'day') <= 1) return
    setStartDate(dayjs(startDate).add(1, 'week').format(dateFormat))
  }, [startDate, dateRange])

  const eventStatus = useMemo(() => (date: string, time: string, index: number) => {
    if (dayjs().diff(`${date} ${time}`, 'minute') > 0) {
      return (
        <td className='close' key={index}>
          -
        </td>
      )
    }
    const event = events?.data.find((schedule) => schedule.date === date)?.events?.find((event) => event.start === time)

    const handleClickEnableModal = () => {
      const selectDatetime = dayjs(`${date} ${time}`).format()
      setEnableModal(true)
      setSelectDatetime(selectDatetime)
    }

    if (event) {
      return (
        <td className='close' key={index}>
          ✕
        </td>
      )
    } else {
      return (
        <td className='open' onClick={handleClickEnableModal} key={index}>
          ○
        </td>
      )
    }
  }, [dateRange, events])

  const handleClickCancel = useCallback(() => {
    setEnableModal(false)
    setSelectDatetime(undefined)
  }, [])


  const handleClickSubmit = useCallback(async () => {
    if (!user.name || !user.email) return
    setSubmitLoading(true)

    await axios.post(
      `${apiUrl}/events/create`,
      {
        email: user.email,
        name: user.name,
        start: selectDatetime,
        end: dayjs(selectDatetime).add(30, 'minute').format(),
      }
    ).then(() => {
      refetch()
      setReserveSuccess(true)
      setSubmitLoading(false)
    })

  }, [selectDatetime, user])

  const handleChangeName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUser({ ...user, name: e.target.value })
  }, [user])

  const handleChangeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUser({ ...user, email: e.target.value })
  }, [user])


  return (
    <>
      <div className='schedule-container'>
        <div className='schedule-table-wrapper'>
          <div className='schedule-table-header'>
            {dayjs(dateRange[0]).diff(initialStartDate, 'day') <= 0 ? (
              <div className='disable-btn'>
                前の週
              </div>
            ) : (
              <div className='prev' onClick={handleClickPrevWeek}>
                前の週
              </div>
            )}
            <div className='date-range'>
              <span>{startDate} ~ {dateRange[dateRange.length - 1]}</span>
            </div>
            {dayjs(endDate).diff(dateRange[dateRange.length - 1], 'day') <= 1 ? (
              <div className='disable-btn'>
                次の週
              </div>
            ) : (
              <div className='next' onClick={handleClickNextWeek}>
                次の週
              </div>
            )}
          </div>

          <div className='schedule-table-container'>
            {loading ? (
              <div>
                <FontAwesomeIcon icon={faSpinner} spin={true} /> loading...
              </div>) : (
              <table>
                <thead>
                  <tr>
                    <th></th>
                    {dateRange.map((date, index) => (
                      <th key={index} className={dayjs().format(dateFormat) === date ? 'active' : 'passive'}>
                        {dayjs(date).format('MM/DD(ddd)')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeRange.map((time, index) => (
                    <tr key={index}>
                      <td className='time'>
                        <span>
                          {time}
                        </span>
                      </td>
                      {dateRange.map((date, index) => (
                        eventStatus(date, time, index)
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {enableModal && (
        <div className='modal-container'>
          <div className='modal-background' onClick={handleClickCancel} />
          {reserveSuccess ? (
            <div className='reserve-form-container'>
              <div className='reserve-form-inner'>
                <div className='reserve-form-header'>
                  <h3>無料カウンセリング予約</h3>
                  <p>
                    以下の日時で予約が完了しました。
                  </p>
                </div>
                <div className='reserve-contents'>
                  <div className='reserve-row'>
                    <div>日時</div>
                    <div>
                      {dayjs(selectDatetime).format('MM月DD日（ddd） HH:mm 〜 ')}
                      {dayjs(selectDatetime).add(30, 'minute').format('HH:mm')}
                    </div>
                  </div>
                </div>
              </div>

              <div className='reserve-form-inner'>
                <div className='btn-grp'>
                  <div className='cancel btn' onClick={handleClickCancel}>
                    <div>閉じる</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='reserve-form-container'>
              <div className='reserve-form-inner'>
                <div className='reserve-form-header'>
                  <h3>無料カウンセリング予約</h3>
                  <p>
                    以下の日時で予約いたします。<br />
                    ※カウンセリングは15分〜30分ほどお時間いただきます。
                  </p>
                </div>
                <div className='reserve-contents'>
                  <div className='reserve-row'>
                    <div>日時</div>
                    <div>
                      {dayjs(selectDatetime).format('MM月DD日（ddd） HH:mm 〜 ')}
                      {dayjs(selectDatetime).add(30, 'minute').format('HH:mm')}
                    </div>
                  </div>

                  <div className='reserve-row'>
                    <div>
                      <div>お名前</div>
                    </div>
                    <div>
                      <div>
                        <input type='text' onChange={handleChangeName} />
                      </div>
                    </div>
                  </div>

                  <div className='reserve-row'>
                    <div>
                      <div>メールアドレス</div>
                    </div>
                    <div>
                      <div>
                        <input type='email' onChange={handleChangeEmail} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='reserve-form-inner'>
                <div className='reserve-row btn-grp'>
                  <div className='cancel btn' onClick={handleClickCancel}>
                    <div>キャンセル</div>
                  </div>
                  <div className={`submit btn ${!enableSubmit && 'disabled'}`} onClick={handleClickSubmit}>
                    {submitLoading ? (
                      <FontAwesomeIcon icon={faSpinner} spin={true} />
                    ) : (
                      <div>確定</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default App